import { useState, useEffect } from 'react';
import { generateThreadFromText } from './utils';
import { event } from './GoogleAnalytics';

const ThreadGenerator = () => {
  const [threadText, setThreadText] = useState('');
  const [generatedThreadPosts, setGeneratedThreadPosts] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [threadStyle, setThreadStyle] = useState('standard');
  const [lastGeneratedStyle, setLastGeneratedStyle] = useState(null);
  
  // Add random seed for each generation
  const addRandomSeed = (text) => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 10);
    return `${text} [uid:${timestamp}:${random}:${threadStyle}]`;
  };

  // Clear generated content when style changes to avoid confusion
  useEffect(() => {
    if (lastGeneratedStyle && lastGeneratedStyle !== threadStyle && generatedThreadPosts.length > 0) {
      setGeneratedThreadPosts([]);
    }
  }, [threadStyle]);

  const generateThread = async (isRegeneration = false) => {
    if (!threadText.trim()) {
      alert('Please enter some text to generate a thread');
      return;
    }

    setLoadingThread(true);
    try {
      event({ 
        action: isRegeneration ? 'regenerate_thread' : 'generate_thread', 
        category: 'engagement', 
        label: `thread_generator_${threadStyle}` 
      });
      
      // Add a random seed to force different results
      const modifiedText = addRandomSeed(threadText);
      
      // Add temperature adjustment for regeneration
      const tempAdjustment = isRegeneration ? 0.05 : 0;
      
      const threadPosts = await generateThreadFromText(modifiedText, isRegeneration, threadStyle, tempAdjustment);
      
      // Save the style we used for this generation
      setLastGeneratedStyle(threadStyle);
      
      // Update the generated posts
      setGeneratedThreadPosts(threadPosts);
      
      event({
        action: isRegeneration ? 'thread_regenerated' : 'thread_generated',
        category: 'success',
        label: `${threadStyle}_posts_count_${threadPosts.length}`,
      });
    } catch (error) {
      console.error("Error generating thread:", error);
      alert("Error generating thread. Please try again.");
      event({ action: 'thread_error', category: 'error', label: error.message });
    } finally {
      setLoadingThread(false);
    }
  };

  // Function to copy post text to clipboard
  const copyPostToClipboard = (post, index) => {
    navigator.clipboard.writeText(post).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000); // Reset after 2 seconds
      event({ action: 'copy_thread_post', category: 'engagement', label: 'thread_post' });
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  return (
    <div className="bg-gray-900 rounded-xl p-4 md:p-6 mb-8 shadow-md">
      <h2 className="text-xl font-bold mb-4">Create X Thread from Text</h2>
      
      <div className="flex justify-end mb-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setThreadStyle('standard')}
            className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
              threadStyle === 'standard'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
            }`}
          >
            Standard
          </button>
          <button
            onClick={() => setThreadStyle('exciting')}
            className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
              threadStyle === 'exciting'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
            }`}
          >
            Exciting 🚀
          </button>
          <button
            onClick={() => setThreadStyle('detailed')}
            className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
              threadStyle === 'detailed'
                ? 'bg-purple-600 text-white shadow-lg'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
            }`}
          >
            Detailed 📊
          </button>
          <button
            onClick={() => setThreadStyle('journalist')}
            className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
              threadStyle === 'journalist'
                ? 'bg-green-600 text-white shadow-lg'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
            }`}
          >
            Journalist 🎯
          </button>
          <button
            onClick={() => setThreadStyle('troll')}
            className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
              threadStyle === 'troll'
                ? 'bg-red-500 text-white shadow-lg'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
            }`}
          >
            Troll 🤪
          </button>
          <button
            onClick={() => setThreadStyle('cryptobro')}
            className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
              threadStyle === 'cryptobro'
                ? 'bg-yellow-500 text-white shadow-lg'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
            }`}
          >
            Crypto Bro 💎
          </button>
        </div>
      </div>
      
      <div className="flex flex-col gap-4">
        <textarea
          className="flex-grow bg-black border border-gray-800 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
          placeholder="Enter your text to be converted into a thread..."
          value={threadText}
          onChange={(e) => setThreadText(e.target.value)}
        />
        
        <div className="flex gap-2">
          <button
            onClick={() => generateThread(false)}
            disabled={loadingThread || !threadText.trim()}
            className={`flex-1 px-6 py-3 rounded-md font-medium transition-colors ${
              loadingThread || !threadText.trim()
                ? 'bg-gray-700 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {loadingThread ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating...
              </span>
            ) : generatedThreadPosts.length > 0 && lastGeneratedStyle === threadStyle ? (
              <>Generate New Thread</>
            ) : (
              <>Generate {threadStyle === 'standard' ? 'Thread' : `${threadStyle.charAt(0).toUpperCase() + threadStyle.slice(1)} Thread`}</>
            )}
          </button>
          
          {generatedThreadPosts.length > 0 && lastGeneratedStyle === threadStyle && (
            <button
              onClick={() => generateThread(true)}
              disabled={loadingThread || !threadText.trim()}
              className={`px-6 py-3 rounded-md font-medium transition-colors ${
                loadingThread
                  ? 'bg-gray-700 cursor-not-allowed'
                  : 'bg-green-500 hover:bg-green-600'
              }`}
            >
              {loadingThread ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Regenerating...
                </span>
              ) : (
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Regenerate
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Display generated thread posts */}
      {generatedThreadPosts.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-3">Your Generated Thread:</h3>
          <div className="space-y-4">
            {generatedThreadPosts.map((post, index) => (
              <div key={index} className="bg-gray-800 p-4 rounded-lg relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-blue-400 font-medium">Post {index + 1}/{generatedThreadPosts.length}</span>
                  <button
                    onClick={() => copyPostToClipboard(post, index)}
                    className="text-gray-400 hover:text-white transition-colors flex items-center"
                  >
                    {copiedIndex === index ? (
                      <span className="text-green-400 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Copied!
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                        Copy
                      </span>
                    )}
                  </button>
                </div>
                
                <p className="whitespace-pre-wrap">{post}</p>
                
                {/* Character count indicator */}
                <div className={`mt-2 text-xs ${post.length > 250 ? 'text-yellow-400' : 'text-gray-500'}`}>
                  {post.length}/280 characters
                </div>
              </div>
            ))}
          </div>
          
          {/* Regenerate tip if style doesn't match */}
          {lastGeneratedStyle !== threadStyle && generatedThreadPosts.length > 0 && (
            <div className="mt-4 bg-yellow-900 p-3 rounded-md text-yellow-200 text-sm">
              <p>⚠️ <strong>Note:</strong> You've changed the style. Click "Generate {threadStyle.charAt(0).toUpperCase() + threadStyle.slice(1)} Thread" to create a new thread in this style.</p>
            </div>
          )}
          
          {/* Thread tips */}
          <div className="mt-4 bg-gray-800 p-3 rounded-md text-gray-400 text-sm">
            <p>💡 <strong>Tip:</strong> Copy each post individually and paste it into your X composer to create the thread.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThreadGenerator;